import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Installation addresses and equipment for wired subscribers (radius-wiki
 * subscriber-cart-v1.md §10, §10.1; owner 2026-10-14/15). One billing address
 * per account; each login (the mobile login and the device login) has an
 * installation location, default "same as billing"; the equipment placed there
 * (router / ONU) is recorded, and a removal sets a date -- never a delete.
 * Hotspot plans carry none of this.
 *
 * radaccess owns the routes; radbilling (servicelocationapi) stores the
 * locations and equipment. Every JSON field name of the contract lives in this
 * one file, so a change in radaccess is one edit here.
 */

// ---------------------------------------------------------------- wire shapes

export interface AddressInput {
  address: string;
  city: string;
  state: string;
  /** 6 digits (Indian PIN code). */
  pin: string;
}

/**
 * radbilling's fixed vocabulary (model/serviceequipment.go validDeviceTypes),
 * stored upper-case; radaccess accepts all seven. "ONU" is shown, "ONT" is sent.
 */
export type DeviceType = "ROUTER" | "ONT" | "WIFI_AP" | "SWITCH" | "MEDIA_CONVERTER" | "SET_TOP_BOX" | "OTHER";
export const ROUTER: DeviceType = "ROUTER";
export const ONU: DeviceType = "ONT";
export const OTHER: DeviceType = "OTHER";
export const DEVICE_TYPES: { value: DeviceType; label: string }[] = [
  { value: ROUTER, label: "Router" },
  { value: ONU, label: "ONU" },
  { value: "WIFI_AP", label: "Wi-Fi access point" },
  { value: "SWITCH", label: "Switch" },
  { value: "MEDIA_CONVERTER", label: "Media converter" },
  { value: "SET_TOP_BOX", label: "Set-top box" },
  { value: OTHER, label: "Other" },
];

export function deviceTypeLabel(t: string): string {
  const u = (t ?? "").toUpperCase();
  return DEVICE_TYPES.find((d) => d.value === u)?.label ?? (u === "ONU" ? "ONU" : t || "—");
}

export interface EquipmentInput {
  device_type: DeviceType;
  make?: string;
  model?: string;
  serial?: string;
  mac_address?: string;
}

/** One login's installation in the Create subscriber body (and the later POST …/installations). */
export interface InstallationInput {
  username: string;
  same_as_billing: boolean;
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  equipment?: EquipmentInput[];
}

/** The Create subscriber body's additions for a wired plan. */
export interface CreateInstallationsBody {
  billing_address: AddressInput;
  installations: InstallationInput[];
}

/** One piece of equipment, as GET …/installations lists it. */
export interface InstalledEquipment {
  id: string;
  device_type: string;
  make?: string;
  model?: string;
  serial?: string;
  mac_address?: string;
  /** RFC 3339; null when billing did not say. */
  installed_at: string | null;
  /** RFC 3339; absent or null while in place. */
  removed_at?: string | null;
}

export interface InstallationLocation {
  id: string;
  label?: string;
  /** The login this location serves. */
  username?: string;
  /** RFC 3339; null when unknown. */
  created_at?: string | null;
  address?: string;
  city?: string;
  state: string;
  pin?: string;
}

/** One login with its latest location and that location's equipment (removed ones included). */
export interface LoginInstallation {
  username: string;
  location: InstallationLocation | null;
  equipment: InstalledEquipment[];
}

export interface Installations {
  /** The account's billing address, when radaccess returns it. */
  billing_address?: AddressInput | null;
  logins: LoginInstallation[];
}

// ---------------------------------------------------------------- routes

const base = (ns: string, accountId: string) =>
  `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/installations`;

export const installationsPath = (ns: string, accountId: string) => base(ns, accountId);
export const equipmentPath = (ns: string, accountId: string, locationId: string) =>
  `${base(ns, accountId)}/${encodeURIComponent(locationId)}/equipment`;
export const removeEquipmentPath = (ns: string, accountId: string, locationId: string, equipmentId: string) =>
  `${equipmentPath(ns, accountId, locationId)}/${encodeURIComponent(equipmentId)}/remove`;

/** A list with null arrays (Go's nil slice) read as empty. */
export function normaliseInstallations(raw: Partial<Installations> | null): Installations {
  const logins = (raw?.logins ?? []).map((l) => ({
    username: l.username ?? "",
    location: l.location ?? null,
    equipment: l.equipment ?? [],
  }));
  return { billing_address: raw?.billing_address ?? null, logins };
}

export async function getInstallations(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<Installations> {
  const r = await callGateway<Partial<Installations> | null>(identity, config().gatewayBase, installationsPath(ns, accountId), signal ? { signal } : {});
  return normaliseInstallations(r.data);
}

/** One location for one login (no equipment: that is added after, with its own call). */
export async function addInstallation(identity: CallerIdentity, ns: string, accountId: string, body: InstallationInput): Promise<InstallationLocation | null> {
  const r = await callGateway<InstallationLocation | null>(identity, config().gatewayBase, installationsPath(ns, accountId), { method: "POST", body });
  return r.data;
}

export async function addEquipment(identity: CallerIdentity, ns: string, accountId: string, locationId: string, body: EquipmentInput): Promise<InstalledEquipment | null> {
  const r = await callGateway<InstalledEquipment | null>(identity, config().gatewayBase, equipmentPath(ns, accountId, locationId), { method: "POST", body });
  return r.data;
}

/**
 * Sets removed_at (now); the row is kept. Refusals carry the server's
 * message: 404 "no such equipment at this installation", 409 "this equipment
 * was removed already".
 */
export async function removeEquipment(identity: CallerIdentity, ns: string, accountId: string, locationId: string, equipmentId: string): Promise<void> {
  await callGateway<unknown>(identity, config().gatewayBase, removeEquipmentPath(ns, accountId, locationId, equipmentId), { method: "PUT" });
}

// ---------------------------------------------------------------- the form

export interface AddressForm {
  address: string;
  city: string;
  state: string;
  pin: string;
}

export const EMPTY_ADDRESS: AddressForm = { address: "", city: "", state: "", pin: "" };

export interface EquipmentForm {
  device_type: DeviceType;
  make: string;
  model: string;
  serial: string;
  mac_address: string;
}

export interface InstallationForm {
  /** "Installed at the billing address"; default on. */
  same_as_billing: boolean;
  address: AddressForm;
  equipment: EquipmentForm[];
}

export const DEFAULT_INSTALLATION: InstallationForm = { same_as_billing: true, address: EMPTY_ADDRESS, equipment: [] };

/** Which login of the Create form: the mobile login, or the device login (if a device id was entered). */
export type LoginRole = "mobile" | "device";

/**
 * A new equipment row. On the device login an ONU row's MAC is pre-filled
 * with the device id (owner §10: "the device login's MAC pre-fills the ONU's
 * MAC"); the operator may still change it.
 */
export function newEquipment(role: LoginRole, deviceid: string, type?: DeviceType): EquipmentForm {
  const t = type ?? (role === "device" ? ONU : ROUTER);
  return { device_type: t, make: "", model: "", serial: "", mac_address: t === ONU && role === "device" ? deviceid.trim() : "" };
}

/** Change a row's type; switching to ONU on the device login fills an empty MAC with the device id. */
export function withDeviceType(row: EquipmentForm, type: DeviceType, role: LoginRole, deviceid: string): EquipmentForm {
  const mac = type === ONU && role === "device" && !row.mac_address.trim() ? deviceid.trim() : row.mac_address;
  return { ...row, device_type: type, mac_address: mac };
}

const trimAddress = (a: AddressForm): AddressInput => ({ address: a.address.trim(), city: a.city.trim(), state: a.state.trim(), pin: a.pin.trim() });

export function addressBody(a: AddressForm): AddressInput {
  return trimAddress(a);
}

/** Empty optional fields left out. */
export function equipmentBody(e: EquipmentForm): EquipmentInput {
  const out: EquipmentInput = { device_type: e.device_type };
  const make = e.make.trim();
  const model = e.model.trim();
  const serial = e.serial.trim();
  const mac = e.mac_address.trim();
  if (make) out.make = make;
  if (model) out.model = model;
  if (serial) out.serial = serial;
  if (mac) out.mac_address = mac;
  return out;
}

/** One login's entry: same_as_billing, or its own address; equipment only when rows were added. */
export function installationBody(username: string, f: InstallationForm): InstallationInput {
  const out: InstallationInput = { username, same_as_billing: f.same_as_billing };
  if (!f.same_as_billing) Object.assign(out, trimAddress(f.address));
  if (f.equipment.length) out.equipment = f.equipment.map(equipmentBody);
  return out;
}

export function addressErrors(a: AddressForm, what: string): string[] {
  const out: string[] = [];
  if (!a.address.trim()) out.push(`${what}: enter the address`);
  if (!a.city.trim()) out.push(`${what}: enter the city`);
  if (!a.state.trim()) out.push(`${what}: enter the state`);
  // Sent as a JSON string; an Indian PIN code never starts with 0.
  if (!/^[1-9]\d{5}$/.test(a.pin.trim())) out.push(`${what}: A PIN code has 6 digits and does not start with 0`);
  return out;
}

const MAC = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$|^([0-9A-Fa-f]{4}\.){2}[0-9A-Fa-f]{4}$|^[0-9A-Fa-f]{12}$/;

/** radbilling refuses a box with neither serial nor MAC, and an unparseable MAC. */
export function equipmentErrors(e: EquipmentForm, what: string): string[] {
  const out: string[] = [];
  const serial = e.serial.trim();
  const mac = e.mac_address.trim();
  if (!serial && !mac) out.push(`${what}: enter a serial number or a MAC address`);
  if (mac && !MAC.test(mac)) out.push(`${what}: MAC address must look like AA:BB:CC:DD:EE:FF`);
  return out;
}

export function installationErrors(f: InstallationForm, username: string): string[] {
  const out: string[] = [];
  if (!f.same_as_billing) out.push(...addressErrors(f.address, `Installation for ${username}`));
  f.equipment.forEach((e, i) => out.push(...equipmentErrors(e, `Equipment ${i + 1} for ${username}`)));
  return out;
}

// ---------------------------------------------------------------- partial create (424)

/**
 * A 424 from Create subscriber whose body carries `installations_done`: the
 * subscriber and plan are live; these logins' installations landed. Read as a
 * list of usernames, or of objects carrying `username`. null when the body has
 * no such field (some other 424).
 */
export function installationsDone(body: unknown): string[] | null {
  if (!body || typeof body !== "object" || !("installations_done" in body)) return null;
  const raw = (body as { installations_done?: unknown }).installations_done;
  if (raw == null) return [];
  if (!Array.isArray(raw)) return null;
  return raw
    .map((x) => (typeof x === "string" ? x : x && typeof x === "object" && "username" in x ? String((x as { username: unknown }).username) : ""))
    .filter(Boolean);
}

/** The logins sent whose installation did not land. */
export function installationsMissing(sent: InstallationInput[] | undefined, done: string[]): string[] {
  const got = new Set(done);
  return (sent ?? []).map((i) => i.username).filter((u) => !got.has(u));
}

export function isInstallationsPartial(e: unknown): boolean {
  return e instanceof ApiError && e.status === 424 && installationsDone(e.body) !== null;
}

// ---------------------------------------------------------------- the view

export function isRemoved(e: InstalledEquipment): boolean {
  return Boolean(e.removed_at);
}

export function inPlace(l: LoginInstallation): InstalledEquipment[] {
  return l.equipment.filter((e) => !isRemoved(e));
}

export function removed(l: LoginInstallation): InstalledEquipment[] {
  return l.equipment.filter(isRemoved);
}

export function addressLine(a: Partial<AddressInput> | null | undefined): string {
  if (!a) return "";
  return [a.address, a.city, [a.state, a.pin].filter(Boolean).join(" ")].map((s) => (s ?? "").trim()).filter(Boolean).join(", ");
}

/** "14 Oct 2026" in the viewer's zone; the raw value when unreadable. */
export function dateLabel(rfc3339: string | null | undefined): string {
  if (!rfc3339) return "—";
  const t = Date.parse(rfc3339);
  if (Number.isNaN(t)) return rfc3339;
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(t);
}
